import React, { useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Upload, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import api, { API_BASE, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function ProImportDialog({ open, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const reset = () => { setFile(null); setResult(null); if (inputRef.current) inputRef.current.value = ""; };
  const close = () => { reset(); onClose?.(); };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Please choose a CSV file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/pro-compliances/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.created > 0) {
        toast.success(`Imported ${data.created} PRO record(s)${data.errors?.length ? ` · ${data.errors.length} row error(s)` : ""}`);
        onDone?.();
      } else if (!data.errors?.length) {
        toast.info("Nothing to import.");
      } else {
        toast.error("No rows imported. See errors below.");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import failed");
    } finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display">Import PRO Compliance from CSV</DialogTitle>
          <DialogDescription>
            Upload a CSV with the required columns. Responsible person must exist in Users;
            client will be auto-linked (or created) from Client Master.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
          <div className="font-medium text-slate-800 mb-1">Required columns</div>
          <div className="font-mono-num text-[11px] break-all">
            compliance_type, client_name, responsible_person_name, professional_fees, expiry_date, reminder_days, status, remark
          </div>
          <a
            href={`${API_BASE}/pro-compliances/import/template.csv`}
            target="_blank"
            rel="noreferrer"
            className="mt-2 inline-flex items-center gap-1 text-blue-700 hover:underline text-xs font-medium"
            data-testid="pro-import-template-link"
          >
            <Download className="w-3.5 h-3.5" /> Download CSV template
          </a>
        </div>

        <form onSubmit={submit} className="space-y-4 mt-2" data-testid="pro-import-form">
          <label className="flex items-center justify-center gap-3 w-full rounded-lg border-2 border-dashed border-slate-300 bg-white hover:border-slate-400 hover:bg-slate-50 transition-colors py-8 cursor-pointer">
            <Upload className="w-5 h-5 text-slate-500" />
            <div className="text-sm">
              {file ? <span className="font-medium text-slate-800 font-mono-num">{file.name}</span> :
                <><span className="text-slate-700 font-medium">Choose CSV file</span><span className="text-slate-500"> or drag it here</span></>}
            </div>
            <input ref={inputRef} type="file" accept=".csv,text/csv" className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="pro-import-file" />
          </label>
          {result && (
            <div className="rounded-md border border-slate-200 bg-white p-3 text-sm space-y-1.5" data-testid="pro-import-result">
              <div className="flex items-center gap-2 text-emerald-700">
                <CheckCircle2 className="w-4 h-4" />
                <span>Created <span className="font-mono-num font-medium">{result.created}</span> record(s)</span>
              </div>
              {result.errors?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 text-red-700 mt-1">
                    <AlertCircle className="w-4 h-4" />
                    <span>Errors on {result.errors.length} row(s):</span>
                  </div>
                  <ul className="max-h-40 overflow-auto mt-1 text-xs font-mono-num text-red-700 pl-6 list-disc">
                    {result.errors.map((er) => <li key={`err-${er.row}-${er.error}`}>Row {er.row}: {er.error}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={close}>Close</Button>
            <Button type="submit" disabled={!file || uploading} data-testid="pro-import-submit">
              {uploading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Import
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
